//
// Copyright (c) 2011-2025 by Curalate, Inc.
//
import { PojoMap } from 'pojo-maps';

import {
  IInstagramStoryGroupAggregateStats,
  IInstagramStoryLinkData,
  InstagramStorySearchSortKey,
} from '@apis/gateway-instagram/instagramStoryAnalyticsApiModel';
import Cuid from '@models/Cuid';
import DateString from '@models/DateString';
import {
  IInstagramStoryClip,
  IInstagramStoryGroup,
} from '@models/IInstagramMediaItem';
import { IUnisonDateRange } from '@models/UnisonTypes';
import { UniversalSearchSortDirection } from '@models/UniversalSearchSortDirection';
import { Normalized } from '@utils/normalize';

export const INSTAGRAM_ACCOUNT_STORY_GROUPS_PAGE_SIZE = 8;

export const storyGroupSortKeySelectors = {
  [InstagramStorySearchSortKey.CompletionRate]: (sg: IInstagramStoryGroup) =>
    sg.storyGroupMetrics.completionRate ?? 0,
  [InstagramStorySearchSortKey.Date]: (sg: IInstagramStoryGroup) =>
    +sg.startTime,
  [InstagramStorySearchSortKey.Reach]: (sg: IInstagramStoryGroup) =>
    sg.storyGroupMetrics.reach ?? 0,
  [InstagramStorySearchSortKey.SwipeUps]: (sg: IInstagramStoryGroup) =>
    sg.storyGroupMetrics.swipeUps ?? 0,
  [InstagramStorySearchSortKey.Revenue]: (sg: IInstagramStoryGroup) =>
    sg.storyGroupMetrics.revenue ?? 0,
  [InstagramStorySearchSortKey.Shares]: (sg: IInstagramStoryGroup) =>
    sg.storyGroupMetrics.shareCount ?? 0,
  [InstagramStorySearchSortKey.Views]: (sg: IInstagramStoryGroup) =>
    sg.storyGroupMetrics.views ?? 0,
} as const;

export type ValidStoryGroupSortKeys = keyof typeof storyGroupSortKeySelectors;

export interface IInstagramAccountStoryGroupsEngagementState {
  flags: {
    isFetchingStoryClips: boolean;
    isFetchingStoryGroups: boolean;
  };
  data: {
    // Story groups for the current date range.
    // We will query both via DateString & via ordered list.
    storyGroups: Normalized<IInstagramStoryGroup, DateString>;

    // Aggregate stats to power the stat bubbles on Story Engagement page.
    aggregateStoryStats?: IInstagramStoryGroupAggregateStats;
    aggregateStoryComparisonStats?: IInstagramStoryGroupAggregateStats;

    // Lookup clip stats by Cuid, for powering Media Cards on daily performance page.
    storyClips: PojoMap<Cuid, IInstagramStoryClip>;

    // Link data for the current story groups
    linkData?: IInstagramStoryLinkData[];

    // Link data for the current story groups grouped by date
    linkDataByDateString?: PojoMap<DateString, IInstagramStoryLinkData[]>;
  };
  ui: {
    // UI data for the state of sorting dropdown.
    storyGroupsSortKey: ValidStoryGroupSortKeys;
    storyGroupsSortDirection: UniversalSearchSortDirection;
    dateRange: IUnisonDateRange;
    storyGroupsPageOffset: number;
  };
}
